/**
 * Paginated consultation form (one question per screen).
 *
 * Runs alongside mini-consultation-form.js — same API, same payload, same
 * localStorage keys that product pages read to unlock "Add to cart".
 * Mounted by sections/hp-consultation-paginated.liquid.
 */
document.addEventListener("DOMContentLoaded", function () {
    const root = document.getElementById("consultation-paginated-app");
    if (!root || !window.Vue) return;

    const API_BASE = "https://admin.happypharmacy.co.uk";
    const MIN_AGE = 18;
    const MAX_AGE = 85;
    const DEFAULT_BMI_THRESHOLD = 27;
    // Show the retry state rather than an endless spinner on a stalled connection
    const FETCH_TIMEOUT_MS = 15000;

    const config = {
        consultationType: root.dataset.consultationType || "weight-loss",
        subscribeDiscount: parseFloat(root.dataset.subscribeDiscount || "0") || 0,
        // Groups whose questions are collected on the final "Review and submit" screen
        reviewGroups: (root.dataset.reviewGroups || "")
            .split(",")
            .map(s => s.trim().toLowerCase())
            .filter(Boolean),
        // Review-screen questions shown as a yellow callout card (matched on question text)
        highlightQuestions: (root.dataset.highlightQuestions || "")
            .split(",")
            .map(s => s.trim().toLowerCase())
            .filter(Boolean),
    };

    // Same keys as mini-consultation-form.js so product pages recognise a submission
    const SUBMITTED_STATE_KEY = `consultationFormState_${config.consultationType}`;
    // Separate key for in-progress answers so the two forms never overwrite each other's drafts
    const DRAFT_KEY = `consultationFormDraftPaginated_${config.consultationType}`;

    // Product IDs unlocked for purchase after submitting, per consultation type
    // (kept in step with mini-consultation-form.js loadProducts()).
    const PRODUCT_ACCESS_IDS = {
        "weight-loss": ["7723490082878", "7724985417790", "7724986826814", "7724986925118", "8106529488958", "8232132444222"],
        "hair-loss": ["7724988104766", "7724988170302"],
        "medication": ["7724988235838"],
        "erectile-dysfunction": ["7724987973694", "7724988006462", "7724988039230", "7724986990654", "7774371840062", "7790407254078", "7790407745598"],
    };

    // Presentation copy for the results screen, keyed by Shopify product_id.
    // Price, title, image and link always come from the API.
    const PRODUCT_DETAILS = {
        "7723490082878": {
            order: 1,
            badge: {label: "Most popular", tone: "pink"},
            name: "Mounjaro",
            generic: "(tirzepatide injection)",
            tagline: "Clinically proven to help you lose weight.",
            bullets: ["Clinically proven results", "Once weekly injection", "Up to 22.5% average weight loss at 72 weeks in a clinical study"],
        },
        "8232132444222": {
            order: 2,
            badge: {label: "Available now", tone: "blue"},
            name: "Foundayo",
            generic: "(orforglipron)",
            tagline: "A once-daily weight loss pill.",
            bullets: ["Clinically proven results", "Taken once daily", "Up to 12.4% average weight loss at 72 weeks in a clinical study"],
        },
        "7724985417790": {
            order: 3,
            name: "Wegovy",
            generic: "(semaglutide)",
            tagline: "Clinically proven to help you lose weight.",
            bullets: ["Clinically proven results", "Once weekly injection", "14.9% average weight loss at 68 weeks in a clinical study"],
        },
        "8106529488958": {
            order: 4,
            name: "Wegovy",
            nameSuffix: "Pills",
            generic: "(semaglutide)",
            tagline: "The first and only pill to contain semaglutide.",
            bullets: ["An effective oral alternative", "Taken once daily", "13.6% average weight loss at 64 weeks in a clinical study"],
        },
    };

    // Subtitles for question types whose API note is usually empty
    const TYPE_SUBTITLES = {
        dob: "We use this to check whether treatment is suitable for your age.",
        height: "We use your height and weight to calculate your BMI and help assess whether treatment is suitable for you.",
        weight: "We use your height and weight to calculate your BMI and help assess whether treatment is suitable for you.",
        gp_lookup: "Sharing your GP details helps our clinical team coordinate your care.",
    };

    const readStorage = (key) => {
        try {
            return JSON.parse(localStorage.getItem(key) || "null");
        } catch (e) {
            return null;
        }
    };
    const writeStorage = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.warn("Could not save consultation state", e);
        }
    };

    const stripHtml = (html) => {
        const el = document.createElement("div");
        el.innerHTML = html || "";
        return el.textContent.replace(/ /g, " ").trim();
    };

    /** Split a note made of <p>/<br> lines into plain-text lines. */
    const noteLines = (html) => {
        if (!html) return [];
        return html
            .split(/<br\s*\/?>|<\/p>|<\/li>/i)
            .map(stripHtml)
            .filter(Boolean);
    };

    const isNoneOption = (option) => /^none\b/i.test((option?.answer_text || "").trim());

    Vue.createApp({
        // Markup lives in a <script type="text/x-template"> so crawlers only see the server-rendered intro
        template: root.dataset.template,
        delimiters: ["[[", "]]"],
        data() {
            return {
                consultationType: config.consultationType,
                consultationGroups: [],
                flatQuestions: [],
                products: [],
                formData: {},
                formAdvisory: {},
                formConfirmation: {},
                formAdditionalDetails: {},
                formWarning: {},
                additionalDetails: {},
                productsToExcludeMap: {},
                previewImages: {},
                dob: {day: "", month: "", year: ""},
                unitSystem: "imperial",
                ageLimits: {min: MIN_AGE, max: MAX_AGE},
                gpSearchPostcode: "",
                gpResults: [],
                gpSearching: false,
                gpSearched: false,
                manualGPEntry: false,
                manualGP: {name: "", address: "", phone: "", email: ""},
                stepIndex: 0,
                attemptedSteps: {},
                isLoading: true,
                loadError: false,
                isSubmitting: false,
                submitError: false,
                isFormSubmitted: false,
                dateSubmitted: null,
                formGuid: null,
            };
        },
        computed: {
            questionById() {
                return Object.fromEntries(this.flatQuestions.map(q => [q.id, q]));
            },

            groupNameByQuestionId() {
                const map = {};
                this.consultationGroups.forEach(group => {
                    (group.questions || []).forEach(q => (map[q.id] = group.name));
                });
                return map;
            },

            /**
             * Build the screens from the API questions:
             * - a first single-option checkbox becomes the "Before you begin" screen
             * - questions with display conditions join the screen of the question they depend on
             * - height and weight share one screen
             * - questions in the configured review groups form the final screen
             */
            steps() {
                const steps = [];
                const stepByQuestionId = {};
                const review = {kind: "review", key: "review", questions: []};
                let intro = null;

                this.flatQuestions.forEach((question, index) => {
                    if (index === 0 && this.isSingleCheckbox(question)) {
                        intro = {kind: "intro", key: "intro", questions: [question]};
                        stepByQuestionId[question.id] = intro;
                        return;
                    }

                    const parentId = question.display_options?.[0]?.depends_on_question_id;
                    const parentStep = parentId && stepByQuestionId[parentId];
                    if (parentStep) {
                        parentStep.questions.push(question);
                        stepByQuestionId[question.id] = parentStep;
                        return;
                    }

                    const groupName = (this.groupNameByQuestionId[question.id] || "").toLowerCase();
                    if (config.reviewGroups.includes(groupName)) {
                        review.questions.push(question);
                        stepByQuestionId[question.id] = review;
                        return;
                    }

                    const previous = steps[steps.length - 1];
                    const isBodyMeasure = ["height", "weight"].includes(question.question_type);
                    if (isBodyMeasure && previous && previous.questions.some(q =>
                        ["height", "weight"].includes(q.question_type) && q.question_type !== question.question_type)) {
                        previous.questions.push(question);
                        stepByQuestionId[question.id] = previous;
                        return;
                    }

                    const step = {kind: "question", key: `q-${question.id}`, questions: [question]};
                    steps.push(step);
                    stepByQuestionId[question.id] = step;
                });

                steps.forEach((step, i) => (step.number = i + 1));
                return [intro, ...steps, review.questions.length ? review : null].filter(Boolean);
            },

            questionStepCount() {
                return this.steps.filter(s => s.kind === "question").length;
            },

            currentStep() {
                return this.steps[this.stepIndex] || null;
            },

            visibleStepQuestions() {
                return this.currentStep ? this.currentStep.questions.filter(q => this.isQuestionVisible(q)) : [];
            },

            progressPercent() {
                const step = this.currentStep;
                if (!step || step.kind === "intro") return 0;
                if (step.kind === "review") return 100;
                return Math.round((step.number / this.questionStepCount) * 100);
            },

            isMeasurementStep() {
                return this.visibleStepQuestions.some(q => ["height", "weight"].includes(q.question_type));
            },

            heightQuestion() {
                return this.flatQuestions.find(q => q.question_type === "height");
            },

            weightQuestion() {
                return this.flatQuestions.find(q => q.question_type === "weight");
            },

            dobQuestion() {
                return this.flatQuestions.find(q => q.question_type === "dob");
            },

            calculateBMI() {
                if (!this.heightQuestion || !this.weightQuestion) return null;
                const cm = parseFloat(this.formData[this.heightQuestion.id]?.cm);
                const kg = parseFloat(this.formData[this.weightQuestion.id]?.kg);
                if (!(cm > 0) || !(kg > 0)) return null;
                const m = cm / 100;
                return kg / (m * m);
            },

            bmiThreshold() {
                let threshold = DEFAULT_BMI_THRESHOLD;
                this.flatQuestions.forEach(question => {
                    if (!this.isQuestionVisible(question)) return;
                    this.selectedOptions(question).forEach(option => {
                        if (!option.lowers_bmi_threshold) return;
                        // Recent weight-loss medication use drops the threshold further
                        threshold = Math.min(threshold, question.id === 587 ? 21.5 : 23.5);
                    });
                });
                return threshold;
            },

            isBMITooLow() {
                return this.calculateBMI !== null && this.calculateBMI < this.bmiThreshold;
            },

            /**
             * Answers on later screens (e.g. weight-related conditions) can lower the BMI
             * threshold, so BMI is only enforced once every screen that affects it is done.
             */
            bmiCheckStepIndex() {
                let index = -1;
                this.steps.forEach((step, i) => {
                    const affectsBmi = step.questions.some(q =>
                        ["height", "weight"].includes(q.question_type) ||
                        (q.options || []).some(o => o.lowers_bmi_threshold));
                    if (affectsBmi) index = i;
                });
                return index;
            },

            calculateAge() {
                if (!this.dobQuestion) return null;
                const dob = this.formData[this.dobQuestion.id];
                if (!dob) return null;
                const [year, month, day] = dob.split("-").map(Number);
                const today = new Date();
                let age = today.getFullYear() - year;
                if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) {
                    age--;
                }
                return age;
            },

            isAgeInvalid() {
                const age = this.calculateAge;
                return age !== null && (age < MIN_AGE || age > MAX_AGE);
            },

            currentStepErrors() {
                return this.currentStep ? this.stepErrors(this.stepIndex) : {};
            },

            showErrors() {
                return Boolean(this.currentStep && this.attemptedSteps[this.currentStep.key]);
            },

            showBMIBlock() {
                return this.showErrors && this.stepIndex === this.bmiCheckStepIndex && this.isBMITooLow;
            },

            excludedProductIds() {
                const ids = new Set();
                Object.entries(this.productsToExcludeMap).forEach(([key, productIds]) => {
                    const questionId = Number(key.split("_")[0]);
                    const question = this.flatQuestions.find(q => q.id === questionId);
                    if (question && !this.isQuestionVisible(question)) return;
                    (productIds || []).forEach(id => ids.add(String(id)));
                });
                return ids;
            },

            visibleProducts() {
                return this.products
                    .filter(product => !this.excludedProductIds.has(String(product.product_id)))
                    .map(product => {
                        const details = PRODUCT_DETAILS[product.product_id] || {};
                        const price = parseFloat(product.price);
                        const subscribePrice = config.subscribeDiscount > 0 && price > 0
                            ? Math.round(price * (100 - config.subscribeDiscount)) / 100
                            : null;
                        return {
                            ...product,
                            details,
                            displayName: details.name || product.title,
                            priceLabel: price > 0 ? price.toFixed(2) : product.price,
                            subscribePriceLabel: subscribePrice ? subscribePrice.toFixed(2) : null,
                        };
                    })
                    .sort((a, b) => (a.details.order || 99) - (b.details.order || 99));
            },
        },
        watch: {
            dob: {
                deep: true,
                handler() {
                    if (!this.dobQuestion) return;
                    this.formData[this.dobQuestion.id] = this.composeDob();
                },
            },
            stepIndex() {
                this.saveDraft();
                this.trackStepView();
                this.$nextTick(() => {
                    const top = this.$refs.formTop;
                    if (top && top.getBoundingClientRect().top < 0) {
                        top.scrollIntoView({behavior: "smooth", block: "start"});
                    }
                });
            },
        },
        methods: {
            /* ---------- Loading ---------- */

            async fetchConsultationData() {
                this.isLoading = true;
                this.loadError = false;
                try {
                    const response = await fetch(`${API_BASE}/api/v1/consultation/${this.consultationType}`, {
                        signal: AbortSignal.timeout?.(FETCH_TIMEOUT_MS),
                    });
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const data = await response.json();
                    this.consultationGroups = data.consultation.question_groups || [];
                    this.products = data.consultation.products || [];
                    this.flatQuestions = this.consultationGroups.flatMap(group => group.questions || []);
                    this.initialiseFormData();
                } catch (error) {
                    console.error("Error fetching consultation data:", error);
                    this.loadError = true;
                } finally {
                    this.isLoading = false;
                }
            },

            initialiseFormData() {
                const draft = readStorage(DRAFT_KEY) || {};
                const savedAnswers = draft.formData || {};

                this.flatQuestions.forEach(question => {
                    let empty = "";
                    if (question.question_type === "height") empty = {cm: "", ft: "", in: ""};
                    else if (question.question_type === "weight") empty = {kg: "", st: "", lbs: ""};
                    else if (question.question_type === "checkbox") empty = [];
                    else if (question.question_type === "file") empty = null;
                    this.formData[question.id] = savedAnswers[question.id] ?? empty;
                });

                this.additionalDetails = draft.additionalDetails || {};
                this.formConfirmation = draft.formConfirmation || {};
                if (draft.unitSystem) this.unitSystem = draft.unitSystem;

                if (this.dobQuestion && this.formData[this.dobQuestion.id]) {
                    const [year, month, day] = this.formData[this.dobQuestion.id].split("-");
                    this.dob = {day: day || "", month: month || "", year: year || ""};
                }

                this.rebuildOptionState();

                // Resume where the patient left off, but never past the first unanswered screen
                const savedIndex = this.steps.findIndex(step => step.key === draft.stepKey);
                if (savedIndex > 0) {
                    const firstInvalid = this.steps.findIndex((step, i) => i < savedIndex && Object.keys(this.stepErrors(i)).length);
                    this.stepIndex = firstInvalid === -1 ? savedIndex : firstInvalid;
                }
            },

            saveDraft() {
                if (!this.flatQuestions.length) return;
                writeStorage(DRAFT_KEY, {
                    formData: this.formData,
                    additionalDetails: this.additionalDetails,
                    formConfirmation: this.formConfirmation,
                    unitSystem: this.unitSystem,
                    stepKey: this.currentStep?.key,
                });
            },

            /* ---------- Question helpers ---------- */

            stripHtml,
            isNoneOption,

            noteItems(question) {
                return noteLines(question.note);
            },

            /** Drop inline styles pasted into the admin (Word/mso fonts, sizes) */
            cleanHtml(html) {
                return (html || "").replace(/\sstyle="[^"]*"/gi, "");
            },

            isHighlighted(question) {
                return config.highlightQuestions.includes(stripHtml(question.text).toLowerCase());
            },

            isSingleCheckbox(question) {
                return question.question_type === "checkbox" && (question.options || []).length === 1;
            },

            isYesNo(question) {
                return question.question_type === "radio" && (question.options || []).length === 2;
            },

            isQuestionVisible(question) {
                if (!question) return true;
                if (!question.display_options || question.display_options.length === 0) return true;

                return question.display_options.reduce((result, condition) => {
                    // A follow-up is hidden whenever the question it depends on is hidden
                    const parent = this.questionById[condition.depends_on_question_id];
                    if (parent && parent !== question && !this.isQuestionVisible(parent)) return false;

                    const dependentAnswer = this.formData[condition.depends_on_question_id];
                    if (dependentAnswer === undefined || dependentAnswer === null) return false;
                    const conditionMet = this.evaluateCondition(
                        condition.operator,
                        dependentAnswer,
                        condition.id_value,
                        condition.value
                    );
                    return condition.logical_operator === "AND" ? result && conditionMet : result || conditionMet;
                }, true);
            },

            evaluateCondition(operator, answer, idValues, values) {
                switch (operator) {
                    case "=":
                        return (idValues && idValues.includes(String(answer))) || (values && values.includes(String(answer)));
                    case "!=":
                        return (idValues && !idValues.includes(String(answer))) && (values && !values.includes(String(answer)));
                    case "exists":
                        return Boolean(answer) === values[0];
                    case "includes":
                        return Array.isArray(answer) && answer.some(a => idValues.includes(a));
                    case "not_includes":
                        return Array.isArray(answer) && !answer.some(a => idValues.includes(a));
                    default:
                        console.warn(`Unsupported operator: ${operator}`);
                        return false;
                }
            },

            selectedOptions(question) {
                const answer = this.formData[question.id];
                const values = Array.isArray(answer) ? answer : [answer];
                return (question.options || []).filter(o => values.includes(o.answer_value));
            },

            isSelected(question, option) {
                const answer = this.formData[question.id];
                return Array.isArray(answer) ? answer.includes(option.answer_value) : answer === option.answer_value;
            },

            subtitleFor(question) {
                if (question.note && !this.noteAsList(question)) return null; // note rendered as HTML instead
                if (question.question_type === "checkbox" && !this.isSingleCheckbox(question)) return "Select all that apply.";
                return TYPE_SUBTITLES[question.question_type] || null;
            },

            /**
             * Notes that are a plain list (e.g. medications, conditions) are shown as a
             * two-column bullet list. Returns {heading, items} or null.
             */
            noteAsList(question) {
                if (question.question_type !== "radio") return null;
                const lines = noteLines(question.note);
                if (lines.length < 3) return null;
                const heading = /:$/.test(lines[0]) ? lines[0] : null;
                const items = heading ? lines.slice(1) : lines;
                if (items.some(line => line.length > 90)) return null;
                return {heading, items};
            },

            /** "White – e.g. British, Irish…" → title "White", hint "e.g. British, Irish…" */
            optionParts(option) {
                const text = (option.answer_text || "").trim();
                const match = text.match(/^(.+?)\s+[–-]\s+(.+)$/);
                return match ? {title: match[1], hint: match[2]} : {title: text, hint: null};
            },

            additionalDetailsLabel(question) {
                const option = this.selectedOptions(question).find(o => o.requires_additional_details);
                return option?.additional_details_label || "Please give us more details";
            },

            needsAdditionalDetails(question) {
                return Object.keys(this.formAdditionalDetails[question.id] || {}).length > 0;
            },

            advisoriesFor(question) {
                return Object.values(this.formAdvisory[question.id] || {});
            },

            needsConfirmation(question) {
                return this.advisoriesFor(question).some(a => a.requiresConfirmation);
            },

            hasWarning(question) {
                return Object.keys(this.formWarning[question.id] || {}).length > 0;
            },

            /* ---------- Answer handling ---------- */

            applyOptionState(question, option, selected) {
                const qid = question.id;
                const setEntry = (map, value) => {
                    if (!map[qid]) map[qid] = {};
                    if (value) map[qid][option.id] = value;
                    else delete map[qid][option.id];
                };

                setEntry(this.formAdvisory, selected && option.advisory_message
                    ? {message: option.advisory_message, requiresConfirmation: option.requires_advisory_confirmation}
                    : null);
                setEntry(this.formWarning, selected && option.risk_level === 100);
                setEntry(this.formAdditionalDetails, selected && option.requires_additional_details);

                const exclusionKey = `${qid}_${option.id}`;
                if (selected && option.exclude_products?.length) {
                    this.productsToExcludeMap[exclusionKey] = option.exclude_products;
                } else {
                    delete this.productsToExcludeMap[exclusionKey];
                }

                if (selected && option.requires_additional_details && this.additionalDetails[qid] === undefined) {
                    this.additionalDetails[qid] = "";
                }
            },

            syncOptionState(question) {
                (question.options || []).forEach(option =>
                    this.applyOptionState(question, option, this.isSelected(question, option)));
                if (!this.needsConfirmation(question)) this.formConfirmation[question.id] = false;
            },

            rebuildOptionState() {
                this.flatQuestions
                    .filter(q => ["radio", "checkbox"].includes(q.question_type))
                    .forEach(q => this.syncOptionState(q));
            },

            selectRadio(question, option) {
                if (this.formData[question.id] !== option.answer_value) {
                    this.formConfirmation[question.id] = false;
                }
                this.formData[question.id] = option.answer_value;
                this.syncOptionState(question);
            },

            toggleCheckbox(question, option) {
                const value = option.answer_value;
                const current = Array.isArray(this.formData[question.id]) ? this.formData[question.id] : [];
                let next;

                if (current.includes(value)) {
                    next = current.filter(v => v !== value);
                } else if (isNoneOption(option)) {
                    next = [value]; // "None of the below" clears everything else
                } else {
                    next = [...current, value].filter(v => {
                        const o = question.options.find(opt => opt.answer_value === v);
                        return !isNoneOption(o);
                    });
                }

                this.formData[question.id] = next;
                this.syncOptionState(question);
            },

            /* ---------- Date of birth ---------- */

            composeDob() {
                const day = parseInt(this.dob.day, 10);
                const month = parseInt(this.dob.month, 10);
                const year = parseInt(this.dob.year, 10);
                if (!day || !month || !year || String(this.dob.year).length !== 4) return "";
                const date = new Date(year, month - 1, day);
                if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return "";
                return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            },

            setDobPart(field, event, maxLength) {
                const digits = event.target.value.replace(/\D/g, "").slice(0, maxLength);
                event.target.value = digits;
                this.dob[field] = digits;
            },

            /* ---------- Height & weight ---------- */

            setUnitSystem(system) {
                this.unitSystem = system;
            },

            convertHeightToFtIn(questionId) {
                const totalInches = parseFloat(this.formData[questionId].cm || 0) / 2.54;
                this.formData[questionId].ft = totalInches ? Math.floor(totalInches / 12) : "";
                this.formData[questionId].in = totalInches ? (totalInches % 12).toFixed(1) : "";
            },

            convertHeightToCm(questionId) {
                const feet = parseFloat(this.formData[questionId].ft || 0);
                const inches = parseFloat(this.formData[questionId].in || 0);
                const totalInches = feet * 12 + inches;
                this.formData[questionId].cm = totalInches ? (totalInches * 2.54).toFixed(1) : "";
            },

            convertWeightToStLbs(questionId) {
                const totalLbs = parseFloat(this.formData[questionId].kg || 0) * 2.20462;
                this.formData[questionId].st = totalLbs ? Math.floor(totalLbs / 14) : "";
                this.formData[questionId].lbs = totalLbs ? (totalLbs % 14).toFixed(1) : "";
            },

            convertWeightToKg(questionId) {
                const stone = parseFloat(this.formData[questionId].st || 0);
                const lbs = parseFloat(this.formData[questionId].lbs || 0);
                const totalLbs = stone * 14 + lbs;
                this.formData[questionId].kg = totalLbs ? (totalLbs / 2.20462).toFixed(1) : "";
            },

            /* ---------- File upload ---------- */

            async handleFileUpload(event, questionId) {
                const file = event.target.files[0];
                if (!file) return;
                try {
                    const presignedResponse = await this.getPresignedUrl(file);
                    await this.uploadToStorage(file, presignedResponse);
                    const fileUrl = `${presignedResponse.url}/${presignedResponse.fields.key}`;
                    this.formData[questionId] = {fileName: file.name, fileUrl};
                    this.previewImages[questionId] = fileUrl;
                } catch (error) {
                    console.error("Error uploading file:", error);
                }
            },

            async getPresignedUrl(file) {
                const response = await fetch(`${API_BASE}/shopify-proxy/presigned_url`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({filename: file.name, contentType: file.type}),
                });
                if (!response.ok) throw new Error("Failed to fetch presigned URL");
                return await response.json();
            },

            async uploadToStorage(file, presignedResponse) {
                const formData = new FormData();
                Object.entries(presignedResponse.fields).forEach(([key, value]) => formData.append(key, value));
                formData.append("file", file);
                const response = await fetch(presignedResponse.url, {method: "POST", body: formData});
                if (!response.ok) throw new Error(`File upload failed with status: ${response.status}`);
            },

            /* ---------- GP lookup ---------- */

            async searchGPs() {
                const query = this.gpSearchPostcode.trim();
                if (query.length < 3) return;
                this.gpSearching = true;
                try {
                    const response = await fetch(`${API_BASE}/api/v1/data/gp?postcode=${encodeURIComponent(query)}`);
                    const data = await response.json();
                    this.gpResults = data.results || [];
                } catch (error) {
                    console.error("Failed to fetch GP list:", error);
                    this.gpResults = [];
                } finally {
                    this.gpSearching = false;
                    this.gpSearched = true;
                }
            },

            selectGP(questionId, gp) {
                this.formData[questionId] = gp;
                this.gpResults = [];
                this.gpSearched = false;
                this.manualGPEntry = false;
            },

            clearGP(questionId) {
                this.formData[questionId] = "";
            },

            selectManualGP(questionId) {
                if (!this.manualGP.name.trim() || !this.manualGP.address.trim()) return;
                this.formData[questionId] = {...this.manualGP};
                this.manualGPEntry = false;
            },

            /* ---------- Validation ---------- */

            questionError(question) {
                if (question.content_type === "rich_content" || !this.isQuestionVisible(question)) return null;
                const answer = this.formData[question.id];

                switch (question.question_type) {
                    case "text":
                    case "textarea":
                        if (!answer || !String(answer).trim()) return "Please enter your answer.";
                        break;
                    case "dob":
                        if (!answer) return "Please enter a valid date of birth.";
                        if (this.isAgeInvalid) return `You must be between ${MIN_AGE} and ${MAX_AGE} years old to continue.`;
                        break;
                    case "checkbox":
                        if (!Array.isArray(answer) || answer.length === 0) {
                            return this.isSingleCheckbox(question) ? "Please tick the box to continue." : "Please select at least one option.";
                        }
                        break;
                    case "file":
                        break;
                    case "height":
                        if (!(parseFloat(answer?.cm) > 0)) return "Please enter your height.";
                        break;
                    case "weight":
                        if (!(parseFloat(answer?.kg) > 0)) return "Please enter your weight.";
                        break;
                    case "gp_lookup":
                        if (!answer) return "Please select your GP practice, or enter the details manually.";
                        break;
                    default:
                        if (!answer) return "Please select an answer.";
                }

                if (["radio", "checkbox"].includes(question.question_type)) {
                    if (this.hasWarning(question)) return "We are unable to continue with this answer.";
                    if (this.needsConfirmation(question) && !this.formConfirmation[question.id]) {
                        return "Please confirm you have read the information above.";
                    }
                    if (this.needsAdditionalDetails(question) && !String(this.additionalDetails[question.id] || "").trim()) {
                        return "Please give us more details.";
                    }
                }
                return null;
            },

            stepErrors(index) {
                const step = this.steps[index];
                const errors = {};
                if (!step) return errors;
                step.questions.forEach(question => {
                    const error = this.questionError(question);
                    if (error) errors[question.id] = error;
                });
                if (index === this.bmiCheckStepIndex && this.isBMITooLow) {
                    errors.bmi = true;
                }
                return errors;
            },

            errorFor(question) {
                return this.showErrors ? this.currentStepErrors[question.id] : null;
            },

            focusFirstError() {
                this.$nextTick(() => {
                    const el = this.$el.querySelector("[data-has-error='true'], [data-bmi-block]");
                    if (el) el.scrollIntoView({behavior: "smooth", block: "center"});
                });
            },

            /* ---------- Navigation ---------- */

            next() {
                const step = this.currentStep;
                if (!step) return;
                this.attemptedSteps[step.key] = true;

                if (Object.keys(this.currentStepErrors).length) {
                    this.focusFirstError();
                    return;
                }

                if (step.kind === "review") {
                    this.submitForm();
                    return;
                }

                this.stepIndex = Math.min(this.stepIndex + 1, this.steps.length - 1);
            },

            back() {
                if (this.stepIndex > 0) this.stepIndex--;
            },

            /* ---------- Submit ---------- */

            submitForm() {
                // Re-check every screen in case earlier answers were changed
                const invalidIndex = this.steps.findIndex((step, i) => Object.keys(this.stepErrors(i)).length);
                if (invalidIndex !== -1) {
                    this.attemptedSteps[this.steps[invalidIndex].key] = true;
                    this.stepIndex = invalidIndex;
                    this.focusFirstError();
                    return;
                }

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "consultation_form_submit",
                    consultation_type: this.consultationType,
                    form_version: "paginated",
                });

                this.isSubmitting = true;
                this.submitError = false;

                fetch(`${API_BASE}/api/v1/consultation/${this.consultationType}`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({
                        guid: this.formGuid,
                        consultationType: this.consultationType,
                        answers: this.formData,
                        additionalDetails: this.additionalDetails,
                    }),
                })
                    .then(response => {
                        if (!response.ok) throw new Error(`API error: ${response.statusText}`);
                        return response.json();
                    })
                    .then(() => {
                        window.dataLayer.push({
                            event: "consultation_form_submitted",
                            consultation_type: this.consultationType,
                            form_version: "paginated",
                        });
                        this.isFormSubmitted = true;
                        this.dateSubmitted = new Date().toISOString();
                        writeStorage(SUBMITTED_STATE_KEY, {
                            formData: this.formData,
                            formAdvisory: this.formAdvisory,
                            formConfirmation: this.formConfirmation,
                            additionalDetails: this.additionalDetails,
                            isFormSubmitted: true,
                            dateSubmitted: this.dateSubmitted,
                        });
                        this.updateProductAccess();
                        this.$nextTick(() => this.$refs.formTop?.scrollIntoView({behavior: "smooth", block: "start"}));
                    })
                    .catch(error => {
                        console.error("Error submitting form:", error);
                        window.dataLayer.push({
                            event: "consultation_form_submit_error",
                            consultation_type: this.consultationType,
                            form_version: "paginated",
                        });
                        this.submitError = true;
                    })
                    .finally(() => {
                        this.isSubmitting = false;
                    });
            },

            updateProductAccess() {
                const data = readStorage("happyPharmacy") || {};
                data.productIdsByType = data.productIdsByType || {};
                data.productIdsByType[this.consultationType] =
                    PRODUCT_ACCESS_IDS[this.consultationType] || this.products.map(p => String(p.product_id));
                writeStorage("happyPharmacy", data);
            },

            resetForm() {
                this.isFormSubmitted = false;
                this.dateSubmitted = null;
                const state = readStorage(SUBMITTED_STATE_KEY) || {};
                state.isFormSubmitted = false;
                state.dateSubmitted = null;
                writeStorage(SUBMITTED_STATE_KEY, state);
                this.attemptedSteps = {};
                this.stepIndex = 0;
            },

            trackStepView() {
                const step = this.currentStep;
                if (!step) return;
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "consultation_step_view",
                    consultation_type: this.consultationType,
                    step: step.kind === "question" ? step.number : step.kind,
                });
            },
        },
        mounted() {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "consultation_form_view",
                consultation_type: this.consultationType,
                form_version: "paginated",
            });

            this.formGuid = localStorage.getItem("hp_form_guid");
            if (!this.formGuid) {
                this.formGuid = crypto.randomUUID();
                localStorage.setItem("hp_form_guid", this.formGuid);
            }

            fetch("/cart/update.js", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({attributes: {form_submission_uuid: this.formGuid}}),
            });

            // A submission in the last 7 days goes straight to the treatment options
            const submitted = readStorage(SUBMITTED_STATE_KEY);
            if (submitted?.isFormSubmitted && submitted.dateSubmitted) {
                const days = (Date.now() - new Date(submitted.dateSubmitted)) / (1000 * 60 * 60 * 24);
                if (days < 7) {
                    this.isFormSubmitted = true;
                    this.dateSubmitted = submitted.dateSubmitted;
                }
            }

            window.addEventListener("pagehide", () => this.saveDraft());

            this.fetchConsultationData().then(() => {
                if (this.isFormSubmitted) {
                    // Restore exclusions from the submitted answers so the right products show
                    const submittedAnswers = readStorage(SUBMITTED_STATE_KEY)?.formData || {};
                    Object.assign(this.formData, submittedAnswers);
                    this.rebuildOptionState();
                }
            });
        },
    }).mount(root);
});
